/**
 * Design tokens — the SINGLE source of truth for the casino look, shared by the
 * R3F canvas (TS material recipes) and the DOM HUD (CSS custom properties).
 *
 * Authored from the `casino-visual-style-guide` skill (palette + PBR recipes +
 * bloom + fonts). The early-2000s luxe-casino realism comes from HDRI image-based
 * lighting + these PBR gold/felt/glass materials + selective bloom — NOT a flat
 * web UI. Keep all magic colors/material params HERE; do not scatter hexes.
 */

/** Named palette (hex). Mirrored to CSS custom properties in `index.css`. */
export const palette = {
  /** Deep matte table green. */
  felt: '#1f7a44',
  /** Shadowed felt / dark panel backdrop. */
  feltDark: '#0d2415',
  /** Scene + page background (near-black warm casino interior). */
  backdrop: '#07120c',
  /** Drapes / accents. */
  velvet: '#5a1622',
  /** Gold gradient stops (highlight → mid → shadow). */
  gold1: '#f7e4a3',
  gold2: '#e7c466',
  gold3: '#b8902f',
  /** Hardware / rails. */
  brass: '#9a7b2e',
  /** Body text on dark. */
  cream: '#f4e9c9',
  /** Polished rail wood. */
  wood: '#4a2c18',
  /** Ornate casino carpet base (deep crimson/burgundy) — fallback if texture absent. */
  carpet: '#3a1220',
  /** Wainscot / wall base (warm dark walnut) — fallback if texture absent. */
  wall: '#241712',
  /** Matte-black VIP card surface. */
  noir: '#0b0b0d',
  /** Padded leather armrest bumper (dark oxblood). */
  leather: '#2a1410',
} as const;

export type PaletteToken = keyof typeof palette;

/**
 * PBR material recipes (Three.js MeshStandardMaterial / MeshPhysicalMaterial
 * props). Spread these onto `<meshStandardMaterial {...} />`. Texture maps are
 * applied separately by the asset stream when present (always guarded).
 */
export const materials = {
  /**
   * Felt — FULLY matte. roughness 1 + metalness 0 + envMapIntensity 0 kills any
   * specular hotspot under the overhead spotlight, so the light reads as a soft
   * diffuse pool on uniform green (no glossy hotspot, no env reflection). The COLOR
   * MAP is intentionally NOT applied to the felt (see Table.tsx): the fabric albedo's
   * woven striations rendered as dark horizontal streak lines. A solid `palette.felt`
   * is clean.
   */
  felt: {
    color: palette.felt,
    roughness: 1,
    metalness: 0,
    envMapIntensity: 0,
  },
  /** Felt in shadow / under-table. */
  feltDark: {
    color: palette.feltDark,
    roughness: 1,
    metalness: 0,
  },
  /**
   * Polished gold/brass. `emissiveIntensity > 1` pushes the bits we WANT to
   * bloom past the selective-bloom luminance threshold; felt stays matte.
   */
  gold: {
    color: palette.gold2,
    metalness: 1,
    roughness: 0.2,
    emissive: palette.gold3,
    emissiveIntensity: 1.4,
  },
  /** Brass — slightly rougher metal for rails/hardware (no bloom). */
  brass: {
    color: palette.brass,
    metalness: 1,
    roughness: 0.35,
    clearcoat: 1,
    clearcoatRoughness: 0.3,
  },
  /** Polished wood rail (clearcoat for the luxe sheen). */
  wood: {
    color: palette.wood,
    roughness: 0.4,
    metalness: 0.05,
    clearcoat: 1,
    clearcoatRoughness: 0.25,
  },
  /** Padded leather armrest bumper — soft sheen, slightly glossy. */
  leather: {
    color: palette.leather,
    roughness: 0.55,
    metalness: 0,
    clearcoat: 0.4,
    clearcoatRoughness: 0.5,
  },
  /** Glass (transmission) recipe for chips/markers. */
  glass: {
    transmission: 1,
    thickness: 0.4,
    ior: 1.5,
    roughness: 0.05,
    metalness: 0,
  },
} as const;

/** Chip denomination colors (cents → felt-friendly chip color). */
export const chipColors: ReadonlyArray<{ readonly minCents: number; readonly color: string }> = [
  { minCents: 10000, color: '#1b1b1b' }, // $100 black
  { minCents: 2500, color: '#6a2fb8' }, // $25 purple
  { minCents: 1000, color: '#2f6ab8' }, // $10 blue
  { minCents: 500, color: '#1f7a44' }, // $5 green
  { minCents: 100, color: '#b83a3a' }, // $1 red
  { minCents: 0, color: '#f4e9c9' }, // sub-$1 cream
];

/** Selective-bloom settings — only emissive > ~1 (gold/text) glows. */
export const bloom = {
  luminanceThreshold: 1.0,
  luminanceSmoothing: 0.2,
  mipmapBlur: true,
  intensity: 0.8,
} as const;

/**
 * Camera defaults for the table view. A LOWER, more behind-the-player 3/4 framing
 * (down from the old near-top-down y=8) so the eye looks ACROSS the felt toward the
 * dealer — the enlarged felt layout (11 betting boxes + arc text) still reads, the
 * whole D stays in frame, AND the 3D High Rollers sign on the back wall sits clearly
 * in the upper background (no longer pinned to the very top edge behind the DOM
 * title). `CameraRig` applies the lookAt on mount, and OrbitControls (Scene.tsx)
 * lets the user tilt up further to read the sign. The clamp limits below are derived
 * from these defaults.
 */
export const camera = {
  // Lower + a touch further back than the old top-down view so the back wall + sign
  // rise into the upper third of the frame while the felt stays fully visible.
  position: [0, 5.6, 9.2] as const,
  /** World point the camera aims at — lifted toward the dealer/back so the look is
   *  more level (less straight-down), pushing the sign down into view. */
  target: [0, 1.4, -1.2] as const,
  fov: 46,
  /**
   * OrbitControls clamp limits. Tuned so the user can pan from looking gently DOWN
   * at the felt to looking UP at the High Rollers sign, but can NEVER flip under or
   * behind the table. Polar angle is measured from +y (0 = straight down the world
   * up-axis looking at the scene from above):
   *   • minPolar ~0.55 rad (~31°)  → tilt UP toward the sign without seeing the void
   *     above the ceiling.
   *   • maxPolar ~1.25 rad (~72°)  → tilt DOWN onto the felt without dropping to a
   *     flat edge-on (which hid the print) or going below the table. (Comfortably
   *     past the default polar of ~68° so the first drag does not snap/jump.)
   * Azimuth is clamped to a small ±0.5 rad sweep so the player stays in front of the
   * curved edge (can't orbit behind the dealer). Distance is clamped so they can't
   * zoom into the felt or out past the room.
   */
  controls: {
    minPolarAngle: 0.55,
    maxPolarAngle: 1.25,
    minAzimuthAngle: -0.5,
    maxAzimuthAngle: 0.5,
    minDistance: 7,
    maxDistance: 13,
  },
};

/**
 * Font families. The asset stream self-hosts the .woff2 files (SIL OFL); we
 * reference the family names here and declare @font-face in index.css with
 * `font-display: swap` so missing files fall back gracefully.
 */
export const fonts = {
  /** Big announcements. */
  display: "'Bungee', system-ui, sans-serif",
  /** Luxe caps headings (Cinzel — elegant classical Roman caps). */
  heading: "'Cinzel', Georgia, serif",
  /** Numerals / labels. */
  numeric: "'Bebas Neue', system-ui, sans-serif",
  /** Body. */
  body: "system-ui, -apple-system, sans-serif",
} as const;

/**
 * Asset paths (served from Pages). These match the files produced by the asset
 * stream in `public/assets/**`. Every load is still GUARDED (Suspense / null
 * fallback) so a missing file degrades to a palette placeholder rather than
 * crashing.
 */
export const assetPaths = {
  cardAtlas: '/assets/cards/atlas.webp',
  cardAtlasMeta: '/assets/cards/atlas.json',
  cardBack: '/assets/cards/back.webp',
  hdri: '/assets/hdri/casino_1k.hdr',
  feltTexture: '/assets/textures/felt/color.webp',
  feltNormal: '/assets/textures/felt/normal.webp',
  feltRoughness: '/assets/textures/felt/roughness.webp',
  woodTexture: '/assets/textures/wood/color.webp',
  woodNormal: '/assets/textures/wood/normal.webp',
  goldTexture: '/assets/textures/gold/color.webp',
  /** Ornate casino floor carpet (asset agent owns these). */
  carpetTexture: '/assets/textures/carpet/color.webp',
  carpetNormal: '/assets/textures/carpet/normal.webp',
  carpetRoughness: '/assets/textures/carpet/roughness.webp',
  /** Wainscot / wall panelling. */
  wallTexture: '/assets/textures/wall/color.webp',
  wallNormal: '/assets/textures/wall/normal.webp',
  wallRoughness: '/assets/textures/wall/roughness.webp',
  /** VIP card-surface UI art (DOM <img>, guarded with onError). */
  uiChip: '/assets/cards-ui/chip.svg',
  uiCrest: '/assets/cards-ui/crest.svg',
  /** Real audio sprite source. webm/mp3 are still stubs (no ffmpeg to transcode),
   *  so we load the WAV directly — browsers decode it fine for Web Audio sprites.
   *  Transcoding to webm/mp3 (smaller) and listing them first is a later size win. */
  sfxWav: '/assets/audio/sfx.wav',
  sfxWebm: '/assets/audio/sfx.webm',
  sfxMp3: '/assets/audio/sfx.mp3',
  sfxMeta: '/assets/audio/sfx.json',
} as const;
